import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter, Routes, Route } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import CookieConsent from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";
import AnalyticsPreditivo from "./pages/AnalyticsPreditivo";
import Blog from "./pages/Blog";
import Resources from "./pages/Resources";
import Comparison from "./pages/Comparison";
import HowItWorksPage from "./pages/HowItWorksPage";
import BlogPostMercado from "./pages/BlogPostMercado";
import BlogPostFalhaIA from "./pages/BlogPostFalhaIA";
import BlogPostProdutividade from "./pages/BlogPostProdutividade";
import BlogPostGestaoChamados from "./pages/BlogPostGestaoChamados";
import BlogPostPlataformaUnificada from "./pages/BlogPostPlataformaUnificada";
import Privacidade from "./pages/Privacidade";
import TermosDeUso from "./pages/TermosDeUso";
import Etica from "./pages/Etica";
import NotFound from "./pages/NotFound";
import Qualidade from "./pages/Qualidade";
import SaudeSeguranca from "./pages/SaudeSeguranca";
import Logistica from "./pages/Logistica";
import Manutencao from "./pages/Manutencao";
import PrevencaoPerdas from "./pages/PrevencaoPerdas";
import Producao from "./pages/Producao";
import GestaoProcessos from "./pages/GestaoProcessos";
import Auditorias from "./pages/Auditorias";
import BlogPostImplementarIA from "./pages/BlogPostImplementarIA";
import BlogPostTendencias2025 from "./pages/BlogPostTendencias2025";
import BlogPostROI from "./pages/BlogPostROI";
import BlogPostAutomacoes from "./pages/BlogPostAutomacoes";
import BlogPostAnalyticsPreditivo from "./pages/BlogPostAnalyticsPreditivo";
import BlogPostIAGenericos from "./pages/BlogPostIAGenericos";
import BlogPostCaseELEEVAIT from "./pages/BlogPostCaseELEEVAIT";
import CaseStudyEleevaIT from "./pages/CaseStudyEleevaIT";
import BlogPostOpenClaw from "./pages/BlogPostOpenClaw";
import BlogPostPlanoAcao from "./pages/BlogPostPlanoAcao";
import BlogPostSolicitacoes from "./pages/BlogPostSolicitacoes";
import Unsubscribe from "./pages/Unsubscribe";

const queryClient = new QueryClient();

// Configure your reCAPTCHA v3 Site Key here
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Test key for development

/** ssrPath is set only by the build-time prerender (src/entry-server.tsx). */
const App = ({ ssrPath }: { ssrPath?: string }) => {
  const content = (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/analytics-preditivo" element={<AnalyticsPreditivo />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/materiais" element={<Resources />} />
        <Route path="/comparar" element={<Comparison />} />
        <Route path="/como-funciona" element={<HowItWorksPage />} />
        <Route path="/blog/mercado-automacao-inteligente" element={<BlogPostMercado />} />
        <Route path="/blog/falha-implementacao-ia" element={<BlogPostFalhaIA />} />
        <Route path="/blog/crise-produtividade" element={<BlogPostProdutividade />} />
        <Route path="/blog/ia-gestao-chamados-2025" element={<BlogPostGestaoChamados />} />
        <Route path="/blog/sinais-plataforma-unificada" element={<BlogPostPlataformaUnificada />} />
        <Route path="/privacidade" element={<Privacidade />} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/etica" element={<Etica />} />
        <Route path="/aplicacoes/qualidade" element={<Qualidade />} />
        <Route path="/aplicacoes/saude-seguranca" element={<SaudeSeguranca />} />
        <Route path="/aplicacoes/logistica" element={<Logistica />} />
        <Route path="/aplicacoes/manutencao" element={<Manutencao />} />
        <Route path="/aplicacoes/prevencao-perdas" element={<PrevencaoPerdas />} />
        <Route path="/aplicacoes/producao" element={<Producao />} />
        <Route path="/aplicacoes/gestao-processos" element={<GestaoProcessos />} />
        <Route path="/aplicacoes/auditorias" element={<Auditorias />} />
        <Route path="/blog/implementar-ia-service-desk" element={<BlogPostImplementarIA />} />
        <Route path="/blog/tendencias-service-management-2025" element={<BlogPostTendencias2025 />} />
        <Route path="/blog/roi-plataformas-unificadas" element={<BlogPostROI />} />
        <Route path="/blog/10-automacoes-service-desk" element={<BlogPostAutomacoes />} />
        <Route path="/blog/analytics-preditivo-futuro-decisao" element={<BlogPostAnalyticsPreditivo />} />
        <Route path="/blog/ia-genericos-nao-funcionam-empresas" element={<BlogPostIAGenericos />} />
        <Route path="/blog/case-eleeva-it-80-reducao-tempo" element={<BlogPostCaseELEEVAIT />} />
        <Route path="/blog/openclaw-itsm-automacao-inteligente" element={<BlogPostOpenClaw />} />
        <Route path="/blog/modulo-plano-de-acao-formularios" element={<BlogPostPlanoAcao />} />
        <Route path="/blog/modulo-solicitacoes-aroan" element={<BlogPostSolicitacoes />} />
        <Route path="/case-study/eleeva-it" element={<CaseStudyEleevaIT />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <CookieConsent />
    </>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {ssrPath === undefined ? (
            <BrowserRouter>{content}</BrowserRouter>
          ) : (
            <MemoryRouter initialEntries={[ssrPath]}>{content}</MemoryRouter>
          )}
        </TooltipProvider>
      </GoogleReCaptchaProvider>
    </QueryClientProvider>
  );
};

export default App;